import { call, select } from 'redux-saga/effects';
import {
  selectGridPanel,
  selectMaximizedStream,
} from 'features/layout/features/content/contentSlice';
import { GridPanelName } from 'features/layout/features/content/types';
import { SourceDetails } from 'features/layout/types';
import {
  selectSelectedScreenshare,
  selectShowAllScreens,
} from 'features/screenshare/screenshareSlice';
import { SelectedScreenshare } from 'features/screenshare/types';
import { pauseScreenshareSaga } from 'features/screenshare/sagas/pauseScreenshareSaga';
import { resumeScreenshareSaga } from 'features/screenshare/sagas/resumeScreenshareSaga';
import { FeedId } from 'features/streaming/types';
import { RTCClient } from 'utils/webrtc';

/**
 * Keeps each remote screenshare subscription in sync with whether the user is actually
 * looking at it. A screenshare is visible when the screenshare panel shows it (single or
 * show-all view) or when it is maximized on the stage. Anything else — whiteboard, files,
 * external streams, an unselected screenshare — stops receiving and resumes when shown again.
 *
 * Participant audio/video are unaffected — those are separate feeds handled by pagination.
 */
export function* reconcileScreenshareVisibilitySaga() {
  const subscribedFeedIds: FeedId[] = yield call(
    RTCClient.receivingFeed.getSubscribedScreenshareFeedIds
  );

  if (!subscribedFeedIds.length) {
    return;
  }

  const gridPanel: GridPanelName | null = yield select(selectGridPanel);
  const showAllScreens: boolean = yield select(selectShowAllScreens);
  const selectedScreenshare: SelectedScreenshare | null = yield select(selectSelectedScreenshare);
  const maximizedStream: SourceDetails | null = yield select(selectMaximizedStream);

  for (const feedId of subscribedFeedIds) {
    const isSelected = selectedScreenshare?.feedId === feedId;
    const visibleInPanel = gridPanel === 'screenshare' && (showAllScreens || isSelected);
    const maximizedOnStage = maximizedStream?.feedId === feedId;

    if (visibleInPanel || maximizedOnStage) {
      // Audio plays only for the screenshare the user is focused on: the selected one in
      // single view or the one maximized on stage. The show-all grid is video-only.
      const hasAudioFocus = maximizedOnStage || (visibleInPanel && !showAllScreens);

      yield call(resumeScreenshareSaga, feedId, { videoOnly: !hasAudioFocus });
    } else {
      yield call(pauseScreenshareSaga, feedId);
    }
  }
}
